import React, { memo, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import Text from 'xpi-black-ui/Base/Typography/Text';
import Input from 'xpi-black-ui/Base/Input';
import Wrapper from './styles';
import { useCallback } from 'react';

function GenericInput({ onChange, title, defaultValue, isRequired, isDisabled, isInvalid, isReadOnly, isPercentage, hasNegativeNumber }) {
  const [value, setValue] = useState(defaultValue);

  const maskLength = useMemo(() => (isPercentage ? 6 : 524288), [isPercentage]);

  const blockKey = useCallback((event) => {
    if (event.defaultPrevented) return;
    if (!hasNegativeNumber && event.key === '-') event.preventDefault();
  }, [hasNegativeNumber]);

  useEffect(() => {
    if (!isReadOnly) {
      let newValue = value || '';

      const HAS_HYPHEN = value?.includes('-');
      if (HAS_HYPHEN || !hasNegativeNumber) newValue.replace('-', '');
      newValue = newValue.replace(/\D/g, '');
      newValue = newValue.replace(/^0+/, '');
      newValue = newValue.substring(0, 14); // max 999.999.999.999,00

      const DECIMAL = 2;
      while (newValue.length < DECIMAL + 1 && newValue.length > 0) {
        newValue = `0${newValue}`;
      }

      let nextStep = 2;
      let nextSeparator = ',';
      while (newValue.length > nextStep) {
        const separatorPosition = newValue.length - nextStep;
        newValue = `${newValue.slice(0, separatorPosition)}${nextSeparator}${newValue.slice(separatorPosition)}`;
        nextStep += DECIMAL * 2;
        nextSeparator = '.';
      }

      const EMPTY_VALUE = `0,${'0'.repeat(DECIMAL)}`;

      if (HAS_HYPHEN && hasNegativeNumber) {
        setValue(newValue === '' ? `-${EMPTY_VALUE}` : `-${newValue}`);
      } else {
        setValue(newValue === '' ? EMPTY_VALUE : newValue);
      }
    }
  }, [isReadOnly, hasNegativeNumber, value]);

  useEffect(() => setValue(defaultValue), [defaultValue]);

  return (
    <Wrapper>
      {title && <Text>{title}</Text>}

      <Input
        onChange={({ target }) => setValue(target.value)} // update input value state
        onKeyUp={({ target }) => onChange(target.value)} // get input value state updated
        onKeyDown={blockKey}
        type="text" // required
        placeholder={defaultValue}
        maxlength={maskLength}
        value={value}
        required={isRequired}
        disabled={isDisabled}
        readOnly={isReadOnly}
        invalid={isInvalid}
      />
    </Wrapper>
  );
}

GenericInput.defaultProps = {
  onChange: () => {},
  title: '',
  defaultValue: '',
  isRequired: false,
  isDisabled: false,
  isInvalid: false,
  isReadOnly: false,
  isPercentage: false,
  hasNegativeNumber: true,
};

GenericInput.propTypes = {
  onChange: PropTypes.func,
  title: PropTypes.string,
  defaultValue: PropTypes.string,
  isRequired: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isPercentage: PropTypes.bool,
  hasNegativeNumber: PropTypes.bool,
};

export default memo(GenericInput);
